import _ from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { InPortal, OutPortal, createHtmlPortalNode } from 'react-reverse-portal';
import {
  CK_BALLOON_DROPDOWN_BUTTON_CONTAINER_CLASS,
  CK_DROPDOWN_BUTTON_CONTAINER_CLASS,
  CONTENT_MODEL_ATTRIBUTES,
  ContentDisplayMode,
} from '@/annotation/ckEditorPlugins/CKEditorPlugins.constants';
import { Root } from '@/core/Root';
import { APPSERVER_API_PREFIX } from '@/main/app.constants';
import { TrendStoreProviders } from '@/trendData/TrendStoreProviders';
import type { Editor, ViewElement } from 'ckeditor5';
import type { ReactElement } from 'react';
import type { Root as IReactRoot } from 'react-dom/client';
import type { HtmlPortalNode } from 'react-reverse-portal';
import type {
  BasePluginDependencies,
  ImageContentListenerCommand,
} from '@/annotation/ckEditorPlugins/CKEditorPlugins.constants';
import type { AssetSelection, Content, DateRange } from '@/reportEditor/report.constants';

const CK_RESET_CLASS = 'ck-reset_all';

export function renderElementWithRoot(
  pluginDependencies: BasePluginDependencies,
  domElement: Element | DocumentFragment,
  componentToRender: React.JSX.Element,
  existingRoot?: IReactRoot,
): IReactRoot {
  const root = existingRoot ?? createRoot(domElement);
  // TrendStoreProviders: these are detached React roots, so converted trend store contexts must be
  // provided here (StoreProviders lives in the main app's tree and cannot reach them).
  root.render(
    <Root {...pluginDependencies}>
      <TrendStoreProviders>{componentToRender}</TrendStoreProviders>
    </Root>,
  );
  return root;
}

/**
 * Renders the given react component inside an InPortal, passing the portalNode the rendered element, and then
 * rendering to the node to an OutPortal at [domElement].
 *
 * @param pluginDependencies - Editor plugin dependencies for the Root that the element is rendered in
 * @param domElement - The dom element the OutPortal will be rendered to
 * @param reactComponent - The actual component being rendered
 * @param props - The props for said component
 * @returns - The floating element the InPortal is created on, allowing for cleanup of the InPortal later on
 */
export function renderElementWithPortal(
  pluginDependencies: BasePluginDependencies,
  domElement: Element | DocumentFragment,
  reactComponent: (props: any) => ReactElement | null,
  props: any,
): IReactRoot {
  const portalNode = createHtmlPortalNode();
  portalNode.element.classList.add('inheritMinHeightAndHeight');
  const portalDomElement = document.createElement('div');

  const root = createRoot(portalDomElement);
  root.render(
    <Root {...pluginDependencies}>
      <InPortal node={portalNode}>
        <Root {...pluginDependencies}>
          <TrendStoreProviders>
            {React.createElement(reactComponent, { ...props, portalNode }, null)}
          </TrendStoreProviders>
        </Root>
      </InPortal>
    </Root>,
  );
  root.render(
    <InPortal node={portalNode}>
      <Root {...pluginDependencies}>
        <TrendStoreProviders>{React.createElement(reactComponent, { ...props, portalNode }, null)}</TrendStoreProviders>
      </Root>
    </InPortal>,
  );
  const rootPortal = createRoot(domElement);
  rootPortal.render(<OutPortal node={portalNode} />);

  return root;
}

export function moveNode(domElement: Element | DocumentFragment, portalNode: HtmlPortalNode) {
  const root = createRoot(domElement);
  root.render(<OutPortal node={portalNode} />);
}

// Some CK image functions needs an imageUtils helper that is annoyingly difficult to grab. We only need one
// function from the helper, so this is it. Confusingly, they use this function for both inline and block, so it
// seems to just be poorly named.
export const replacementImageUtils = {
  isInlineImageView: (node: any) => node && node.is('element', 'img'),
};

/**
 * Taken from the CKEditor imageUtils.isLocalImage function, which is not available in the new export scheme
 */
export function isBase64Image(imageUtils: any, node: any): boolean {
  if (!imageUtils.isInlineImageView(node) || !node.getAttribute('src')) {
    return false;
  }

  return !!node.getAttribute('src')!.match(/^data:image\/\w+;base64,/g) || !!node.getAttribute('src')!.match(/^blob:/g);
}

export function isNonContentImage(node: any) {
  return (
    node.is('element', 'img') &&
    node.getAttribute('src') &&
    !node.getAttribute(CONTENT_MODEL_ATTRIBUTES.DATA_SEEQ_CONTENT)
  );
}

export function forceReloadContent(contentId: string) {
  const contentImg = getContent(contentId);
  if (contentImg) {
    const event = new Event('load');
    contentImg.dispatchEvent(event);
  }
}

/**
 * CK adds the ck-reset_all class to its toolbar which adds a bunch of styles that are hard to override in the
 * dropdowns. When toggling it off, make to sure to re-toggle so that other CK styles work as expected.
 */
export function toggleCkResetClass() {
  const toolbarContainer = document.getElementById('journalEditorToolbarContainer');
  const toolbar = toolbarContainer?.querySelector('.ck-toolbar');
  if (!toolbar) {
    return;
  }
  toolbar.classList.contains(CK_RESET_CLASS)
    ? toolbar.classList.remove(CK_RESET_CLASS)
    : toolbar.classList.add(CK_RESET_CLASS);
}

export function setSeeqContentDropdownMenuHeight(isToolbarButton: boolean) {
  const dropdownPanelButton = window.document.getElementById(
    `${isToolbarButton ? CK_DROPDOWN_BUTTON_CONTAINER_CLASS : CK_BALLOON_DROPDOWN_BUTTON_CONTAINER_CLASS}`,
  );
  if (!dropdownPanelButton) {
    return;
  }

  const dropdownPanelView = dropdownPanelButton.querySelector<HTMLElement>('.ck-dropdown__panel');
  if (!dropdownPanelView) {
    return;
  }

  const dropdownPanelButtonPosition = dropdownPanelButton
    .querySelector<HTMLButtonElement>('button.contentBtn')
    ?.getBoundingClientRect();
  if (!dropdownPanelButtonPosition) {
    return;
  }

  const popoverHeight = window.innerHeight - dropdownPanelButtonPosition.bottom - 30; // 30 is just a little margin from the bottom of the screen
  if (
    dropdownPanelView.classList.contains('ck-dropdown__panel_se') &&
    popoverHeight < dropdownPanelButtonPosition.top
  ) {
    dropdownPanelView.classList.add('ck-dropdown__panel_ne');
    dropdownPanelView.style.maxHeight = `${dropdownPanelButtonPosition.top}px`;
  } else {
    dropdownPanelView.style.maxHeight = `${popoverHeight}px`;
  }
}

export function buildRelativeContentUrl(contentId: string): string {
  return `${APPSERVER_API_PREFIX}/content/${contentId}/sourceUrl`;
}

export function buildAbsoluteContentUrl(
  baseUrl: string,
  content: Content,
  maybeDateRange?: DateRange,
  maybeAssetSelection?: AssetSelection,
): string {
  const url = new URL(`${baseUrl}/view/worksheet/${content.workbookId}/${content.worksheetId}`);
  url.searchParams.append('workstepId', content.workstepId);
  if (maybeDateRange) {
    url.searchParams.append('displayRangeStart', moment.utc(maybeDateRange.range.start).toISOString());
    url.searchParams.append('displayRangeEnd', moment.utc(maybeDateRange.range.end).toISOString());
  }
  if (content.timezone) {
    url.searchParams.append('timezone', content.timezone);
  }
  if (maybeAssetSelection) {
    url.searchParams.append('assetId', maybeAssetSelection.asset.id);
  }
  return url.toString();
}

export function getContentSpecificCommand(command: ImageContentListenerCommand, contentId: string) {
  return `${command}-${contentId}`;
}

/**
 * Uses javascript to find out if content is in the document, because it is possible that contentMetadata is out of
 * sync, and using that metadata to determine if content is in the document can fail. We know that this will give
 * us the most up to date information because it checks what is actually visible to the user.
 *
 * @param contentId for the piece of content we are checking
 */
export function isContentInDocument(contentId: string): boolean {
  return !_.isNil(getContent(contentId));
}

export function getContent(contentId: string): Element | null {
  return document.querySelector(`[${CONTENT_MODEL_ATTRIBUTES.DATA_SEEQ_CONTENT}="${contentId}"]`);
}

export function getContentWidgetViewElement(contentImage: Node, editor: Editor): ViewElement | null {
  const imageView = editor.editing.view.domConverter.domToView(contentImage);
  if (imageView && imageView.is('element')) {
    return (imageView as ViewElement).findAncestor({ name: 'div', classes: 'seeqContentWrapper' });
  }
  return null;
}

export function getContentDisplayMode(canModify: boolean, isPDF: boolean): ContentDisplayMode {
  if (isPDF) {
    return ContentDisplayMode.PDF;
  } else if (!canModify) {
    return ContentDisplayMode.VIEW_ONLY;
  } else {
    return ContentDisplayMode.NORMAL;
  }
}
